import { zeroErrorMessage } from "@/lib/zero-error";
import { useQuery as useZeroQuery, useZero } from "@rocicorp/zero/react";
import { appMutators, appQueries } from "@production-hub/shared";
import { useState } from "react";
import { toast } from "sonner";
import { toFriendlyMutationError } from "@/lib/error-utils";
import { generateUUID } from "@/lib/utils";

export interface EquipmentTag {
  id: string;
  name: string;
  parent_id: string | null;
  created_at: string;
  updated_at: string;
  children?: EquipmentTag[];
}

export interface EquipmentTagTreeNode extends EquipmentTag {
  children: EquipmentTagTreeNode[];
  depth: number;
  fullPath: string;
  searchableText: string;
}

type ZeroMutationResult =
  | { type: "error"; error: { message: string } }
  | { type: string; error?: never };
type ZeroMutateClient = {
  mutate: (mutation: unknown) => {
    client: Promise<void>;
    server: Promise<ZeroMutationResult>;
  };
};
type MutationOptions<T> = { onSuccess?: (data: T) => void };

type ZeroEquipmentTagRow = {
  id: string;
  name: string;
  parentID?: string | null;
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
};

type ZeroEquipmentTagAssignmentRow = {
  tagID: string;
};

function toLegacyTimestamp(value: number | string | null | undefined) {
  if (typeof value === "number") return new Date(value).toISOString();
  return value ?? "";
}

function toLegacyTag(row: ZeroEquipmentTagRow): EquipmentTag {
  return {
    id: row.id,
    name: row.name,
    parent_id: row.parentID ?? null,
    created_at: toLegacyTimestamp(row.createdAt),
    updated_at: toLegacyTimestamp(row.updatedAt),
  };
}

async function reportZeroMutationFailure(
  serverResult: Promise<ZeroMutationResult>,
  fallback: string,
) {
  const result = await serverResult;
  if (result.type === "error") {
    throw new Error(result.error?.message || fallback);
  }
}

function useZeroMutation<TInput, TResult>({
  run,
  success,
  failure,
}: {
  run: (input: TInput) => Promise<TResult>;
  success: string;
  failure: string;
}) {
  const [isPending, setIsPending] = useState(false);
  const wrappedRun = async (input: TInput) => {
    setIsPending(true);
    try {
      return await run(input);
    } catch (error) {
      throw toFriendlyMutationError(error);
    } finally {
      setIsPending(false);
    }
  };
  return {
    mutate: (input: TInput, options?: MutationOptions<TResult>) => {
      void wrappedRun(input)
        .then((data) => {
          toast.success(success);
          options?.onSuccess?.(data);
        })
        .catch((error) => toast.error(`${failure}: ${error.message}`));
    },
    mutateAsync: wrappedRun,
    isPending,
  };
}

const compareByText = (a: string, b: string) =>
  a.localeCompare(b, undefined, { sensitivity: "base" });

const buildTagIndex = (tags: EquipmentTag[]) => {
  const tagMap = new Map(tags.map((tag) => [tag.id, tag]));

  const getAncestors = (tag: EquipmentTag): EquipmentTag[] => {
    const ancestors: EquipmentTag[] = [];
    const visited = new Set<string>();
    let currentParentId = tag.parent_id;

    while (currentParentId && !visited.has(currentParentId)) {
      visited.add(currentParentId);
      const parent = tagMap.get(currentParentId);
      if (!parent) break;
      ancestors.unshift(parent);
      currentParentId = parent.parent_id;
    }

    return ancestors;
  };

  const getFullPath = (tag: EquipmentTag) =>
    [...getAncestors(tag).map((ancestor) => ancestor.name), tag.name].join(
      " > ",
    );

  const getSearchableText = (tag: EquipmentTag) =>
    [...getAncestors(tag).map((ancestor) => ancestor.name), tag.name]
      .join(" ")
      .toLowerCase();

  const childrenByParent = new Map<string | null, EquipmentTag[]>();
  for (const tag of tags) {
    const siblings = childrenByParent.get(tag.parent_id) || [];
    siblings.push(tag);
    childrenByParent.set(tag.parent_id, siblings);
  }

  for (const siblings of childrenByParent.values()) {
    siblings.sort((a, b) => compareByText(getFullPath(a), getFullPath(b)));
  }

  const buildTree = (
    parentId: string | null,
    depth = 0,
  ): EquipmentTagTreeNode[] =>
    (childrenByParent.get(parentId) || []).map((tag) => ({
      ...tag,
      children: buildTree(tag.id, depth + 1),
      depth,
      fullPath: getFullPath(tag),
      searchableText: getSearchableText(tag),
    }));

  const collectDescendantIds = (tagId: string, result: Set<string>) => {
    if (result.has(tagId)) return;
    result.add(tagId);

    for (const child of childrenByParent.get(tagId) || []) {
      collectDescendantIds(child.id, result);
    }
  };

  return {
    tagMap,
    getAncestors,
    getFullPath,
    getSearchableText,
    buildTree,
    collectDescendantIds,
  };
};

export const useEquipmentTags = () => {
  const [data, details] = useZeroQuery(appQueries.equipmentTags.list());
  const tags = ((data ?? []) as ZeroEquipmentTagRow[]).map(toLegacyTag);
  const { getFullPath } = buildTagIndex(tags);

  return {
    data: tags.sort((a, b) => compareByText(getFullPath(a), getFullPath(b))),
    isLoading: details.type !== "complete" && tags.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
};

export const useEquipmentTagsTree = () => {
  const { data: tags, ...rest } = useEquipmentTags();
  const tree = tags ? buildTagIndex(tags).buildTree(null) : undefined;

  return {
    ...rest,
    data: tree,
    flatTags: tags,
  };
};

export const getEquipmentTagDescendantIds = (
  tags: EquipmentTag[],
  selectedTagIds: string[],
) => {
  const { collectDescendantIds } = buildTagIndex(tags);
  const expanded = new Set<string>();

  for (const tagId of selectedTagIds) {
    collectDescendantIds(tagId, expanded);
  }

  return Array.from(expanded);
};

export const useCreateEquipmentTag = () => {
  const zero = useZero() as ZeroMutateClient;
  return useZeroMutation<
    { name: string; parent_id?: string | null },
    EquipmentTag
  >({
    success: "Tag created",
    failure: "Failed to create tag",
    run: async (tag) => {
      const id = generateUUID();
      const now = new Date().toISOString();
      const mutation = zero.mutate(
        appMutators.equipmentTags.create({
          id,
          name: tag.name,
          parentID: tag.parent_id ?? null,
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(mutation.server, "Tag create rejected");
      return {
        id,
        name: tag.name,
        parent_id: tag.parent_id ?? null,
        created_at: now,
        updated_at: now,
      };
    },
  });
};

export const useUpdateEquipmentTag = () => {
  const zero = useZero() as ZeroMutateClient;
  return useZeroMutation<
    {
      id;
      name?: string;
      parent_id?: string | null;
    },
    { id: string }
  >({
    success: "Tag updated",
    failure: "Failed to update tag",
    run: async ({ id, ...updates }) => {
      const mutation = zero.mutate(
        appMutators.equipmentTags.update({
          id,
          name: updates.name,
          parentID: updates.parent_id,
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(mutation.server, "Tag update rejected");
      return { id };
    },
  });
};

export const useEquipmentTagCounts = () => {
  const [data, details] = useZeroQuery(appQueries.equipmentTags.counts());
  const counts: Record<string, number> = {};
  for (const row of (data ?? []) as ZeroEquipmentTagAssignmentRow[]) {
    counts[row.tagID] = (counts[row.tagID] ?? 0) + 1;
  }
  return {
    data: counts,
    isLoading: details.type !== "complete" && !data,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
};

export const useDeleteEquipmentTag = () => {
  const zero = useZero() as ZeroMutateClient;
  return useZeroMutation<string, string>({
    success: "Tag deleted",
    failure: "Failed to delete tag",
    run: async (id) => {
      const mutation = zero.mutate(appMutators.equipmentTags.delete({ id }));
      await mutation.client;
      await reportZeroMutationFailure(mutation.server, "Tag delete rejected");
      return id;
    },
  });
};
